import { PlanTypesEnum } from '@ctct/domain-layer';
import type { ComparisonTableFeature, ComparisonTableGroup } from '@ctct/payflow-ui';

import { contentInternational } from './contentInternational';
import { injectDataComplianceGroup } from './injectDataComplianceGroup';
import type { ContentStrategy } from '../ContentStrategy';

const idToTypeMap = {
  '1': PlanTypesEnum.Bronze,
  '2': PlanTypesEnum.Silver,
  '3': PlanTypesEnum.Gold,
};

export type InternationalCountryCode = 'AU' | 'GB';

/**
 * Shared content strategy for AU and GB customers when eligible for SMS Marketing.
 *
 * Displays "SMS Marketing" (without country restrictions). SMS and inbox-preview
 * pricing are placeholder tokens resolved downstream from country-config.
 *
 * AU customers additionally see the Data & Compliance group; GB customers do not.
 *
 * Only used when: (1) customer is AU or GB (plan group or country code), AND
 * (2) SMS country eligibility endpoint returns true for that market.
 *
 * See specs/sms-international.md for full details.
 */
export class InternationalPricingContentStrategy implements ContentStrategy {
  constructor(private countryCode: InternationalCountryCode) {}

  getInitialGroups(): ComparisonTableGroup[] {
    const sourceGroups =
      this.countryCode === 'AU' ? injectDataComplianceGroup(contentInternational.groups) : contentInternational.groups;

    return sourceGroups.map((group) => ({
      ...group,
      features: group.features.map((feature) => ({
        ...feature,
        parameters: feature.parameters.map((parameter) => ({ ...parameter, id: idToTypeMap[parameter.id] })),
      })),
    }));
  }

  getInitialPlans() {
    return contentInternational.headers.plans;
  }

  getStaticTableContent(): ComparisonTableFeature[] {
    return contentInternational.marketingData.features.slice(1).map((feature) => ({
      ...feature,
      parameters: feature.parameters.map((parameter) => ({ ...parameter, id: idToTypeMap[parameter.id] })),
    }));
  }
}
